import { z } from 'zod';
import { DocumentTypeEnum, PartnerKategorieValues } from '../../document.js';
export const PaymentDecisionStatus = z.enum([
    'draft',
    'in-review',
    'needs-revision',
    'request-feedback',
    'wait-for-feedback',
    'payment-pending',
    'payment-failed',
    'wait-for-bcl',
    'send-notification',
    'update-claim',
    'discarded',
    'completed',
]);
const PaymentDecisionCommonDocumentFields = {
    documentReference: z
        .strictObject({
        id: z.string().min(1),
        timestamp: z.string().min(1),
        fromType: z.enum(PartnerKategorieValues).nullable(),
        fromName: z.string().min(1).nullable(),
        businessName: z.string().min(1).nullable().optional(), // optional for backward compatibility
        type: DocumentTypeEnum.nullable(),
        number: z.string().min(1).nullable(),
        amountEur: z.number().nullable(),
    })
        .nullable(),
    documentType: z.enum(['invoice', 'quote', 'self-repair']),
    documentNumber: z.string().min(1).nullable(),
    businessName: z.string().min(1).nullable().optional(), // optional for backward compatibility
    totalAmountNetEur: z.number(),
    totalAmountGrossEur: z.number(),
    totalTaxEur: z.number(),
    paymentReason: z.string().min(1).nullable(),
    internalNote: z.string().min(1).nullable(),
    /**
     * Whether the issuer bills without VAT as a Kleinunternehmer (§ 19 UStG), which makes a 0 % rate valid; the
     * claim agent sets it from the Beleg, a clerk with the Kleinunternehmerregelung checkbox of the editor.
     * Absent on a Beleg written before the field existed, and counts as false.
     */
    smallBusiness: z.boolean().optional(),
};
const PaymentDecisionCommonLineItemFields = {
    description: z.string().min(1).nullable(),
    priceNetEur: z.number().nullable(),
    priceGrossEur: z.number().nullable(),
    quantity: z.number().nullable(),
    unit: z.string().min(1).nullable(),
    amountNetEur: z.number(),
    amountGrossEur: z.number(),
    taxPercentage: z.number(),
    taxEur: z.number(),
    calculationBasis: z.enum(['net', 'gross']),
};
export const PaymentDecision = z.strictObject({
    type: z.literal('payment-decision'),
    formalCoverage: z.strictObject({
        decision: z.enum(['covered', 'not-covered']).nullable(),
        recordedAt: z.string().min(1),
    }),
    materialCoverage: z.strictObject({
        decision: z.enum(['covered', 'not-covered']).nullable(),
        preliminary: z.unknown().optional(), // backward compatibility
        recordedAt: z.string().min(1),
    }),
    documents: z.array(z.discriminatedUnion('decision', [
        z.strictObject({
            ...PaymentDecisionCommonDocumentFields,
            decision: z.literal('authorization'),
            // Release under reservation: set by the clerk's "vorbehaltlich" checkbox, and always on an
            // authorization the AI writes, draft or release; absent on records written before the field existed
            provisional: z.boolean().optional(),
            lineItems: z
                .strictObject({
                ...PaymentDecisionCommonLineItemFields,
                deductionPercentage: z.number(),
                deductionEur: z.number(),
                authorizationEur: z.number(),
            })
                .array(),
            totalDeductionEur: z.number(),
            totalAuthorizationEur: z.number(),
            appliedDeductibleEur: z.number(),
        }),
        z.strictObject({
            ...PaymentDecisionCommonDocumentFields,
            decision: z.literal('settlement'),
            lineItems: z
                .strictObject({
                ...PaymentDecisionCommonLineItemFields,
                deductionPercentage: z.number(),
                deductionEur: z.number(),
                settlementEur: z.number(),
            })
                .array(),
            totalDeductionEur: z.number(),
            totalSettlementEur: z.number(),
            appliedDeductibleEur: z.number(),
            payoutEur: z.number(),
            bankAccountFeedback: z
                .strictObject({
                providedBy: z.strictObject({
                    role: z.enum(['policy-holder', 'broker']),
                    name: z.string().min(1).nullable(),
                }),
                accountHolder: z.string().min(1),
                iban: z.string().min(1),
            })
                .nullable(),
            bankAccount: z // only for payoutEur > 0
                .strictObject({
                personId: z.string().min(1),
                bankAccountId: z.string().min(1),
                personName: z.string().min(1).nullable(),
                holderName: z.string().min(1).nullable(),
                accountType: z.string().min(1).nullable(),
                accountIban: z.string().min(1).nullable(),
            })
                .nullable(),
            /**
             * The IBAN the payout shall go to: the one the claim's documents name, as the claim agent took it
             * from them, else the IBAN of the account set. It never differs from `bankAccount.accountIban`:
             * setting an account sets it, and an account with another IBAN is cleared. Without an account
             * it is the proposal the clerk searches OASIS with. Null or absent when no IBAN is known.
             */
            bankAccountIban: z.string().min(1).nullable().optional(),
            paymentReference: z.string().nullable().optional(), // optional for backward compatibility
            accountingRecordId: z.string().min(1).nullable(), // Only settlement documents have accounting records
            paymentResult: z.enum(['completed', 'rejected']).nullable().optional(), // optional for backward compatibility
        }),
        z.strictObject({
            ...PaymentDecisionCommonDocumentFields,
            decision: z.literal('denial'),
            lineItems: z
                .strictObject({
                ...PaymentDecisionCommonLineItemFields,
                denialEur: z.number(),
            })
                .array(),
            totalDenialEur: z.number(),
        }),
    ])),
    paymentReason: z.string().min(1).nullable().optional(), // optional for backward compatibility
    internalNote: z.string().min(1).nullable().optional(), // optional for backward compatibility
    aiProtocol: z.string().min(1).nullable().optional(), // optional for backward compatibility
    summary: z.strictObject({
        documents: z
            .strictObject({
            documentNumber: z.string().min(1).nullable(),
            authorizationEur: z.number(),
            authorizationDeductionEur: z.number(),
            settlementEur: z.number(),
            settlementDeductionEur: z.number(),
            denialEur: z.number(),
        })
            .array(),
        totalAuthorizationEur: z.number(),
        totalAuthorizationDeductionEur: z.number(),
        totalAuthorizationAppliedDeductibleEur: z.number(),
        totalSettlementEur: z.number(),
        totalSettlementDeductionEur: z.number(),
        totalSettlementAppliedDeductibleEur: z.number(),
        totalSettlementPayoutEur: z.number(),
        totalDenialEur: z.number(),
    }),
    isSettlementProposal: z.unknown().optional(), // retired settlement-proposal flow; still present on stored records
    proposalFeedback: z.unknown().optional(), // retired settlement-proposal flow; still present on stored records
    assignedReviewer: z
        .strictObject({
        entraId: z.string().min(1),
        name: z.string().min(1).nullable(),
    })
        .nullable(),
    assignedClerk: z
        .strictObject({
        entraId: z.string().min(1),
        name: z.string().min(1).nullable(),
    })
        .nullable()
        .optional(), // optional for backward compatibility
    notificationRecipient: z.union([
        z
            .strictObject({
            email: z.string().min(1),
            role: z.enum(['policy-holder', 'broker']).nullable(),
        })
            .nullable(),
        z
            .strictObject({
            email: z.string().min(1),
            role: z.enum(['policy-holder', 'broker']).nullable(),
        })
            .array(),
    ]),
    approvedBy: z
        .union([
        z.strictObject({
            type: z.literal('ai'),
        }),
        z.strictObject({
            entraId: z.string().min(1),
            name: z.string().min(1).nullable(),
            oasisUsername: z.string().min(1).nullable(),
        }),
    ])
        .nullable(),
    approvedAt: z.string().min(1).nullable(),
    preparedBy: z
        .union([
        z.strictObject({
            type: z.literal('ai'),
        }),
        z.strictObject({
            entraId: z.string().min(1),
            name: z.string().min(1).nullable(),
        }),
    ])
        .nullable(),
    preparedAt: z.string().min(1).nullable(),
    revisionRequest: z.string().min(1).nullable().optional(), // optional for backward compatibility
    processingStatus: z.string().min(1).nullable().optional(), // Bearbeitungsstand - optional for backward compatibility
    closeClaim: z.boolean().optional(), // Schaden schließen - optional for backward compatibility, defaults to false
    sendTrustpilotEmail: z.unknown().optional(), // retired: the clerk's wish for a rating request, which the claim agent now decides alone
});
